
import React, { useState, useEffect } from 'react';
import { TaskTemplate, TaskPackage } from '../types';
import { fetchTemplates, updateTemplates, DEFAULT_TEMPLATES } from '../services/asanaService';
import { Save, RotateCcw, Info, ChevronDown, ChevronUp, Edit3, User, Building2, Zap, Cloud, Loader2, Check, Plus, Trash2, Box } from 'lucide-react';

export const TaskSettings: React.FC = () => {
  const [templates, setTemplates] = useState<TaskTemplate[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [hasSaved, setHasSaved] = useState(false);
  
  const [lastActive, setLastActive] = useState<{ 
    id: string; 
    field: 'name' | 'notes'; 
    cursor: number 
  } | null>(null);

  useEffect(() => {
    if (isOpen) {
        fetchTemplates().then(setTemplates);
    }
  }, [isOpen]);

  const handleSave = async () => {
    setIsSaving(true);
    await updateTemplates(templates);
    setIsSaving(false);
    setHasSaved(true);
    setTimeout(() => {
      setHasSaved(false);
      setIsOpen(false);
    }, 800);
  };

  const handleReset = async () => {
    if (confirm('Reset all tasks to default and sync to cloud?')) {
      setTemplates(DEFAULT_TEMPLATES);
      await updateTemplates(DEFAULT_TEMPLATES);
    }
  };

  const handleAddTask = () => {
    const newId = Date.now().toString();
    const newTask: TaskTemplate = {
      id: newId,
      name: 'New Task',
      notes: '',
      packages: [TaskPackage.PREMIUM, TaskPackage.ELITE, TaskPackage.SUPREME]
    };
    setTemplates(prev => [...prev, newTask]);
    setExpandedId(newId);
  };

  const handleRemoveTask = (e: React.MouseEvent, id: string) => {
    e.stopPropagation();
    if (confirm('Are you sure you want to delete this task template?')) {
      setTemplates(prev => prev.filter(t => t.id !== id));
      if (expandedId === id) setExpandedId(null);
    }
  };

  const updateTemplate = (id: string, field: string, value: any) => {
    setTemplates(prev => prev.map(t => t.id === id ? { ...t, [field]: value } : t));
  };

  const togglePackage = (id: string, pkg: TaskPackage) => {
    setTemplates(prev => prev.map(t => {
      if (t.id === id) {
        const currentPackages = t.packages || [];
        const newPackages = currentPackages.includes(pkg)
          ? currentPackages.filter(p => p !== pkg)
          : [...currentPackages, pkg];
        return { ...t, packages: newPackages };
      }
      return t;
    }));
  };

  const insertShortcode = (code: string) => {
    if (!lastActive) {
      if (templates.length > 0) {
          const firstId = templates[0].id;
          setExpandedId(firstId);
          setLastActive({ id: firstId, field: 'notes', cursor: templates[0].notes.length });
          const currentVal = templates[0].notes;
          updateTemplate(firstId, 'notes', currentVal + code);
      }
      return;
    }

    const { id, field, cursor } = lastActive;
    setTemplates(prev => prev.map(t => {
      if (t.id === id) {
        const currentVal = t[field];
        const newVal = currentVal.slice(0, cursor) + code + currentVal.slice(cursor);
        return { ...t, [field]: newVal };
      }
      return t;
    }));
    setLastActive(prev => prev ? { ...prev, cursor: cursor + code.length } : null);
  };

  const groups = [
    {
      title: 'BUSINESS INFO',
      icon: <Building2 className="w-3 h-3" />,
      codes: [
        { code: '{businessname}', desc: 'Name' },
        { code: '{businessphone}', desc: 'Phone' },
        { code: '{businessemail}', desc: 'Email' },
        { code: '{address}', desc: 'Street' },
        { code: '{city}', desc: 'City' },
        { code: '{state}', desc: 'State (Full)' },
        { code: '{zip}', desc: 'Zip' },
        { code: '{website}', desc: 'Website' },
      ]
    },
    {
      title: 'OWNER INFO',
      icon: <User className="w-3 h-3" />,
      codes: [
        { code: '{ownername}', desc: 'Full Name' },
        { code: '{ownerfirstname}', desc: 'First' },
        { code: '{ownerlastname}', desc: 'Last' },
        { code: '{ownerphone}', desc: 'Phone' },
        { code: '{owneremail}', desc: 'Email' },
      ]
    },
    {
      title: 'AUTOMATION',
      icon: <Zap className="w-3 h-3" />,
      codes: [
        { code: '{agentname}', desc: 'Agent' },
        { code: '{username}', desc: 'Generated User' },
        { code: '{password}', desc: 'Generated Pass' },
      ]
    }
  ];

  if (!isOpen) {
    return (
      <div className="max-w-2xl mx-auto mb-8">
        <button 
          onClick={() => setIsOpen(true)}
          className="w-full flex items-center justify-center gap-2 py-3 bg-white/40 border border-white/40 rounded-xl text-slate-600 hover:bg-white/60 transition-all font-medium shadow-sm hover:shadow-md backdrop-blur-sm"
        >
          <Edit3 className="w-4 h-4" />
          Customize Asana Task Content & Shortcodes
        </button>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto bg-slate-900/60 backdrop-blur-md flex items-center justify-center p-4">
      <div className="glass-panel w-full max-w-6xl rounded-3xl shadow-2xl flex flex-col h-[90vh] overflow-hidden bg-white/80">
        <div className="p-6 border-b border-slate-200/50 flex items-center justify-between bg-white/50 rounded-t-3xl">
          <div>
            <h2 className="text-xl font-bold text-slate-800 flex items-center gap-2">
                Customize Task Templates
                <Cloud className="w-4 h-4 text-blue-500" />
            </h2>
            <p className="text-sm text-slate-500">Updates are synced to Firebase for your entire team.</p>
          </div>
          <div className="flex gap-2">
            <button onClick={handleReset} className="p-2 text-slate-400 hover:text-red-500 transition-colors" title="Reset to Defaults">
              <RotateCcw className="w-5 h-5" />
            </button>
            <button onClick={() => setIsOpen(false)} className="px-4 py-2 text-slate-600 font-medium hover:bg-slate-100/50 rounded-xl transition-colors">Cancel</button>
            <button 
              onClick={handleSave} 
              disabled={isSaving}
              className={`px-8 py-2.5 rounded-xl font-bold shadow-lg flex items-center gap-2 transition-all min-w-[140px] justify-center ${hasSaved ? 'bg-green-600 text-white' : 'bg-gradient-to-r from-blue-600 to-indigo-600 text-white hover:shadow-indigo-500/20 disabled:opacity-50'}`}
            >
              {isSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : hasSaved ? <Check className="w-4 h-4" /> : <Save className="w-4 h-4" />}
              {isSaving ? 'Syncing...' : hasSaved ? 'Saved!' : 'Save & Sync'}
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-hidden grid grid-cols-1 lg:grid-cols-4">
          <div className="lg:col-span-1 border-r border-slate-200/50 bg-slate-50/50 overflow-y-auto p-6 space-y-8">
            <h3 className="text-blue-800 font-bold text-[11px] flex items-center gap-2 uppercase tracking-widest">
              <Info className="w-4 h-4" /> Click to Insert
            </h3>
            {groups.map(group => (
              <div key={group.title} className="space-y-3">
                <div className="flex items-center gap-2 text-[10px] font-extrabold text-slate-400 uppercase tracking-widest">
                  {group.icon} {group.title}
                </div>
                <div className="grid grid-cols-1 gap-1.5">
                  {group.codes.map(s => (
                    <button 
                      key={s.code} 
                      onClick={() => insertShortcode(s.code)}
                      className="text-left bg-white/70 border border-slate-200/50 rounded-xl p-2.5 hover:border-blue-400 hover:bg-blue-50 transition-all group shadow-sm active:scale-95"
                    >
                      <code className="text-[11px] font-mono text-blue-600 font-bold group-hover:text-blue-700">
                        {s.code}
                      </code>
                      <span className="text-[10px] text-slate-400 block mt-0.5">{s.desc}</span>
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>

          <div className="lg:col-span-3 overflow-y-auto p-8 space-y-4 bg-white/40">
            <div className="flex justify-between items-center mb-6">
                <h3 className="text-slate-500 font-bold text-xs uppercase tracking-widest">Manage Tasks ({templates.length})</h3>
                <button 
                    onClick={handleAddTask}
                    className="flex items-center gap-2 px-4 py-2 bg-blue-100 text-blue-700 rounded-xl font-bold text-xs hover:bg-blue-200 transition-all active:scale-95 shadow-sm"
                >
                    <Plus className="w-4 h-4" /> Add New Task
                </button>
            </div>

            {templates.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-20 text-slate-400 gap-3 bg-white/50 rounded-3xl border border-dashed border-slate-300">
                    <p className="font-medium">No task templates found.</p>
                    <button onClick={handleAddTask} className="text-blue-600 font-bold hover:underline flex items-center gap-1">
                        <Plus className="w-4 h-4" /> Create your first task
                    </button>
                </div>
            ) : templates.map((template, idx) => {
              const isExpanded = expandedId === template.id;
              return (
                <div key={template.id} className={`border rounded-2xl transition-all duration-300 overflow-hidden ${isExpanded ? 'border-blue-300 shadow-xl ring-2 ring-blue-100 bg-white/90' : 'border-white/60 bg-white/60 hover:bg-white/80 shadow-sm'}`}>
                  <div className={`w-full flex items-center justify-between p-5 text-left transition-colors cursor-pointer ${isExpanded ? 'bg-blue-50/30' : ''}`} onClick={() => setExpandedId(isExpanded ? null : template.id)}>
                    <div className="flex-1 min-w-0">
                      <span className="text-[9px] font-bold text-slate-400 uppercase tracking-tighter">Task {idx + 1}</span>
                      <h4 className="font-bold text-slate-800 truncate text-lg">{template.name || 'Untitled Task'}</h4>
                    </div>
                    <div className="flex items-center gap-2">
                        <button 
                            onClick={(e) => handleRemoveTask(e, template.id)}
                            className="p-2 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-xl transition-all"
                            title="Delete Task"
                        >
                            <Trash2 className="w-4 h-4" />
                        </button>
                        <div className={`p-2 rounded-xl transition-colors ${isExpanded ? 'bg-blue-100 text-blue-600' : 'bg-slate-100/50 text-slate-400'}`}>
                            {isExpanded ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
                        </div>
                    </div>
                  </div>
                  {isExpanded && (
                    <div className="p-6 pt-2 space-y-5 border-t border-slate-100/50">
                      <div>
                        <label className="block text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-2 ml-1">Assign to Packages</label>
                        <div className="flex flex-wrap gap-2">
                          {Object.values(TaskPackage).map(pkg => (
                            <label 
                              key={pkg} 
                              className={`flex items-center gap-2 px-3 py-1.5 rounded-lg border cursor-pointer transition-all ${template.packages?.includes(pkg) ? 'bg-blue-100 border-blue-300 text-blue-700 shadow-sm' : 'bg-white border-slate-200 text-slate-500 hover:border-blue-200'}`}
                            >
                              <input 
                                type="checkbox" 
                                className="hidden" 
                                checked={template.packages?.includes(pkg)} 
                                onChange={() => togglePackage(template.id, pkg)} 
                              />
                              <span className="text-xs font-bold">{pkg}</span>
                              {template.packages?.includes(pkg) && <Check className="w-3 h-3" />}
                            </label>
                          ))}
                        </div>
                      </div>
                      <div>
                        <label className="block text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-2 ml-1">Task Title</label>
                        <input 
                          type="text"
                          value={template.name}
                          onFocus={() => setLastActive({ id: template.id, field: 'name', cursor: template.name.length })}
                          onBlur={(e) => setLastActive({ id: template.id, field: 'name', cursor: e.target.selectionStart || 0 })}
                          onChange={(e) => updateTemplate(template.id, 'name', e.target.value)}
                          className="glass-input w-full px-4 py-3 rounded-xl outline-none transition-all"
                          placeholder="e.g. New Website Agreement"
                        />
                      </div>
                      <div>
                        <label className="block text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-2 ml-1">Task Notes</label>
                        <textarea 
                          rows={8}
                          value={template.notes}
                          onFocus={() => setLastActive({ id: template.id, field: 'notes', cursor: template.notes.length })}
                          onBlur={(e) => setLastActive({ id: template.id, field: 'notes', cursor: e.target.selectionStart || 0 })}
                          onChange={(e) => updateTemplate(template.id, 'notes', e.target.value)}
                          className="glass-input w-full px-4 py-3 rounded-xl outline-none resize-none transition-all font-sans"
                          placeholder="Instructions for the agent..."
                        />
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};
